import '../styles/ProductionReadinessActivities.scss';

import { Stack } from '@whistler-ui/k2/components/Stack';
import { Heading, Paragraph } from '@whistler-ui/k2/components/Typography';
import { Link } from 'react-router';

import useRenderIf from '../../../hooks/useRenderIf';
import { useThemeStore } from '../../../store/theme';

const componentName = 'c-ProductionReadinessActivities';

export function ProductionReadinessActivities() {
  const { RenderIf } = useRenderIf();

  const { config } = useThemeStore();

  const image = config.assets.onboarding.productionReadiness;

  return (
    <Stack direction="row" gap={8} tag="article" className={componentName}>
      <Stack direction="column" tag="section" gap={8}>
        <Heading>Production Readiness Activities</Heading>
        <Stack direction="column" gap={4}>
          <Paragraph>
            Once you have completed the API and Use Case tests on our Portal
            against your Test or Pre-Production Environment, you can request an
            upgrade to a Production account.
          </Paragraph>
          <Paragraph>
            We will review your API and Use Case test results, and once
            approved, your account will be upgraded.
          </Paragraph>
          <Paragraph>
            Before your Entitlement Server (ES) can go live, we need to certify
            your production implementation. The following steps must be
            completed first:
          </Paragraph>
          <RenderIf ues={false}>
            <ol>
              <li className="my-4">
                <b>Populate your network details:</b> go to{' '}
                <b>{`Entitlements > API Implementation > Network Details`}</b>
              </li>
              <li className="my-4">
                <b>Register a new domain or sub-domain in your name</b> (e.g.
                es.
                {'<operatorname>'}.com) and update the <i>Environment</i> tab of
                your production environment with this info.
              </li>
              <li className="my-4">
                <b>Hosted Zone setup:</b> we will configure a hosted zone for
                your (sub)domain and create ios.
                {'<(sub)domain>'} and android.{'<(sub)domain>'} endpoints for
                iOS and Android, including push notification topics.
              </li>
              <li className="my-4">
                <b>Configure NS records:</b> update your (sub)domain's
                nameserver (NS) records with the details we will provide for
                your hosted zone.
              </li>
            </ol>
          </RenderIf>
          <RenderIf ues={true}>
            <ol>
              <li className="my-4">
                <b>Populate your network details:</b> go to{' '}
                <b>{`Entitlements > API Implementation > Network Details`}</b>
              </li>
              <li className="my-4">
                <b>Register a new domain or sub-domain in your name</b> (e.g.
                es.
                {'<operatorname>'}.com) and update the <i>Environment</i> tab of
                your production environment with this info.
              </li>
              <li className="my-4">
                <b>Hosted Zone setup:</b> we will configure a hosted zone for
                your (sub)domain and create ios.
                {'<(sub)domain>'} and android.{'<(sub)domain>'} endpoints for
                iOS and Android, including push notification topics.
              </li>
              <li className="my-4">
                <b>Configure NS records:</b> update your (sub)domain's
                nameserver (NS) records with the details we will provide for
                your hosted zone.
              </li>
              <li className="my-4">
                <b>Update your GSMA NSX settings:</b> add the iOS ES URL
                https://ios.{'<(sub)domain>'} and Push Notification topics to
                your NSX network settings and upload them to{' '}
                <Link to="/developers?tab=gsma-nsx">GSMA NSX</Link>.{' '}
                <b>
                  <i>
                    <br />
                    We strongly recommend initiating the NSX process early as it
                    often requires several uploads to fully reflect your APN and
                    ES settings.
                  </i>
                </b>
              </li>
              <li className="my-4">
                <b>Send test SIMs</b>: send us 50 eSIM QR codes and 25 physical
                SIM cards that we will use to test and certify your
                implementation. The (e)SIMs have to be pre-provisioned with a
                plan that allows for voice and data.
              </li>
            </ol>
          </RenderIf>
          <Paragraph>
            We’ll collaborate closely with you throughout these steps alongside
            your API implementation to help ensure you meet your Go Live target
            timeline.
          </Paragraph>
          <Paragraph>
            Once your account is upgraded to Production, you can configure your
            Production environment and repeat the API and Use Case tests against
            your production deployment.
          </Paragraph>
          <RenderIf ues={true}>
            <Paragraph>
              Certification testing with iOS devices will start once your
              Production environment has successfully passed the Use Case tests
              on the Developer Portal and we've reviewed the results.
              Certification includes Baseline and eSIM Transfer, which are
              mandatory use cases for iOS.
            </Paragraph>
          </RenderIf>
          <RenderIf ues={false}>
            <Paragraph>
              The OEM will certify your implementation, and we'll support you
              during this process.
            </Paragraph>
          </RenderIf>
        </Stack>
      </Stack>
      {image && (
        <aside>
          <img src={image} alt="Production Readiness" />
        </aside>
      )}
    </Stack>
  );
}
